import { useState } from "react";
import { useErrorTabCount } from "src/Hooks/useErrorTabCount";
import { OrderTablesTabIndex } from "../Order/OrderTablesBox/OrderResult";
import {
  DeltaHedgeOrderType,
  IDeltaHedgeOrderTablesSearchParam,
} from "../../Interfaces/deltaHedgeOrder";
import { useAppDispatch, useAppSelector } from "../../App/hooks";
import DeltaHedgeOrderTable from "../../Tables/DeltaHedgeOrder/Order/DeltaHedgeOrderTable";

import DeltaHedgeOrderTradeTable from "../../Tables/DeltaHedgeOrder/Trade/DeltaHedgeOrderTradeTable";
import ErrorsTable from "../../Tables/Order/ErrorsTable/ErrorsTable";
import {
  getDeltaHedgeOrderDeletedOMSOrders,
  getDeltaHedgeOrderErrorTable,
} from "../../Requests/deltaHedgeOrder";
import Modal from "../../Components/Modal/Modal";
import TabBox from "../../Components/Tabbox/TabBox";
import { DropdownItem } from "../../Interfaces/dropdownInterfaces";
import AddOrderBox from "./AddOrderBox/AddOrderBox";
import EditOrder from "./EditOrder";
import SearchParamBox from "./SearchParamBox";
import { setDeltaHedgeEditFlag } from "../../Features/DeltaHedgeOrder/deltaHedgeOrderSlice";
import Orderbook from "./Orderbook";
// Local Components
import DeltaHedgeAggregationTable from "src/Tables/DeltaHedgeOrder/Order/DeltaHedgeAggregationTable";
import { Button } from "src/Components";
import TableTemplate from "src/Tables/TableTemplate/tableTemplate";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
import DeleteOMSOrderForm from "./DeleteOMSOrderForm";

export const deltaHedgeOrderTypesDropdownOptions: DropdownItem[] = [
  { id: DeltaHedgeOrderType.Open, name: "ورود" },
  { id: DeltaHedgeOrderType.Offset, name: "آفست" },
  { id: DeltaHedgeOrderType.Roll, name: "رول" },
];

enum ModalState {
  Closed,
  DeleteOrder,
  Logs,
}

const DeltaHedgeOrder = () => {
  const [tabIndex, setTabIndex] = useState(
    OrderTablesTabIndex.AggregationTable
  );

  const [tablesSearchParam, setTablesSearchParam] =
    useState<IDeltaHedgeOrderTablesSearchParam>(
      {} as IDeltaHedgeOrderTablesSearchParam
    );
  const [modalState, setModalState] = useState(ModalState.Closed);

  const editFlag = useAppSelector((store) => store.nLegs.editFlag);
  const dispatch = useAppDispatch();

  const { errorTabCount, errorTableRefreshFlag, errorTableAfterFetchHandler } =
    useErrorTabCount(
      "deltaHedgeOrder",
      tabIndex,
      OrderTablesTabIndex.ErrorsTable,
      getDeltaHedgeOrderErrorTable
    );

  const tablesTabsChildren = [
    { index: OrderTablesTabIndex.AggregationTable, title: "سفارشات تجمیعی" },
    { index: OrderTablesTabIndex.OrderTable, title: "سفارشات" },
    { index: OrderTablesTabIndex.TradeTable, title: "معاملات" },
    {
      index: OrderTablesTabIndex.ErrorsTable,
      title: "خطا",
      count: errorTabCount,
    },
  ];

  const searchParamChangeHandler = (name: string, value: any) => {
    setTablesSearchParam({
      ...tablesSearchParam,
      [name]: value,
    });
  };

  const getTabIndexTable = () => {
    switch (tabIndex) {
      case OrderTablesTabIndex.AggregationTable:
        return <DeltaHedgeAggregationTable searchParam={tablesSearchParam} />;
      case OrderTablesTabIndex.OrderTable:
        return <DeltaHedgeOrderTable searchParam={tablesSearchParam} />;
      case OrderTablesTabIndex.TradeTable:
        return <DeltaHedgeOrderTradeTable searchParam={tablesSearchParam} />;
      case OrderTablesTabIndex.ErrorsTable:
        return (
          <ErrorsTable
            getData={getDeltaHedgeOrderErrorTable}
            searchParams={tablesSearchParam}
            onFetchComplete={errorTableAfterFetchHandler}
            refreshFlag={errorTableRefreshFlag}
          />
        );
    }
  };

  const getModalUI = () => {
    switch (modalState) {
      case ModalState.Logs:
        return (
          <TableTemplate
            TableUI={({ reports }) => <CommonTableUI reports={reports} />}
            getDataTable={getDeltaHedgeOrderDeletedOMSOrders}
          />
        );

      case ModalState.DeleteOrder:
        return <DeleteOMSOrderForm />;

      default:
        return <></>;
    }
  };

  return (
    <div className="flex flex-row justify-start items-start gap-5 w-full pr-5 outline-none border-none">
      <AddOrderBox />
      {editFlag ? (
        <Modal title="ویرایش سفارش"
          UI={
            <EditOrder
              isConditionId={tabIndex === OrderTablesTabIndex.AggregationTable}
            />
          }
          onClose={() => {
            dispatch(setDeltaHedgeEditFlag(false));
          }}
        />
      ) : (
        <></>
      )}
      {modalState !== ModalState.Closed ? (
        <Modal title={modalState === ModalState.Logs ? "لاگ حذف سفارشات باز" : "تنظیمات"}
          UI={getModalUI()}
          onClose={() => setModalState(ModalState.Closed)}
        />
      ) : (
        <></>
      )}
      <div className="flex flex-col justify-start items-start w-full">
        <div className="flex flex-row justify-start items-start w-full">
          <Orderbook />

          <div className="flex flex-row justify-start items-start mt-6 gap-3 ml-2 mr-auto">
            <div className="w-min">
              <Button
                text="لاگ حذف سفارشات باز"
                variant="outlined"
                onClick={() => setModalState(ModalState.Logs)}
              />
            </div>
            <div className="w-min">
              <Button
                text="تنظیمات"
                variant="contained"
                onClick={() => setModalState(ModalState.DeleteOrder)}
              />
            </div>
          </div>
        </div>
        <div className="w-full pt-5">
          <div className="max-w-min m-auto">
            <div className="w-full flex flex-row justify-between items-center">
              <div className="max-w-min ml-5">
                <TabBox
                  activeTab={tabIndex}
                  children={tablesTabsChildren}
                  onChange={(name, index) => setTabIndex(index)}
                />
              </div>
              <SearchParamBox
                onChange={searchParamChangeHandler}
                searchParam={tablesSearchParam}
              />
            </div>
            <div className="w-full mt-2">{getTabIndexTable()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeltaHedgeOrder;
